import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MOIS, fmtMontant, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, Users, FileText, FileCheck2 } from "lucide-react";

const ALL = "__all__";

function Filters({ actes, assujettis, provinces, annees, filters, setFilters, showProvince }) {
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Select value={filters.assujetti_id} onValueChange={(v) => upd("assujetti_id", v)}>
        <SelectTrigger data-testid="rep-assujetti"><SelectValue placeholder="Assujetti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les assujettis</SelectItem>
          {assujettis.map((a) => <SelectItem key={a.id} value={a.id}>{a.redevable}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.acte_generateur_id} onValueChange={(v) => upd("acte_generateur_id", v)}>
        <SelectTrigger data-testid="rep-acte"><SelectValue placeholder="Acte générateur" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les actes</SelectItem>
          {actes.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}
        </SelectContent>
      </Select>
      {showProvince && (
        <Select value={filters.province} onValueChange={(v) => upd("province", v)}>
          <SelectTrigger data-testid="rep-province"><SelectValue placeholder="Province" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les provinces</SelectItem>
            {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {!showProvince && (
        <Select value={filters.annee} onValueChange={(v) => upd("annee", v)}>
          <SelectTrigger data-testid="rep-annee"><SelectValue placeholder="Année" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les années</SelectItem>
            {annees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {!showProvince && (
        <Select value={filters.mois} onValueChange={(v) => upd("mois", v)}>
          <SelectTrigger data-testid="rep-mois"><SelectValue placeholder="Mois" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les mois</SelectItem>
            {MOIS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button variant="outline" onClick={() => window.print()} data-testid="rep-print-btn">
        <Printer className="mr-1.5 h-4 w-4" /> Imprimer
      </Button>
    </div>
  );
}

export default function Rapports() {
  const [actes, setActes] = useState([]);
  const [assujettis, setAssujettis] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [tab, setTab] = useState("assujettis");

  const [fa, setFa] = useState({ assujetti_id: ALL, acte_generateur_id: ALL, province: ALL, annee: ALL, mois: ALL });
  const [fd, setFd] = useState({ assujetti_id: ALL, acte_generateur_id: ALL, province: ALL, annee: ALL, mois: ALL });
  const [fp, setFp] = useState({ assujetti_id: ALL, acte_generateur_id: ALL, province: ALL, annee: ALL, mois: ALL });

  const [dataA, setDataA] = useState([]);
  const [dataD, setDataD] = useState([]);
  const [dataP, setDataP] = useState([]);

  useEffect(() => {
    api.get("/actes").then((r) => setActes(r.data));
    api.get("/assujettis").then((r) => setAssujettis(r.data));
    api.get("/geo/provinces").then((r) => setProvinces(r.data));
    api.get("/dashboard").then((r) => setAnnees(r.data.annees_disponibles));
  }, []);

  const buildParams = (f) => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== ALL) p[k] = v; });
    return p;
  };

  const loadA = useCallback(async () => {
    const { data } = await api.get("/assujettis", { params: buildParams(fa) });
    setDataA(data);
  }, [fa]);
  const loadD = useCallback(async () => {
    const { data } = await api.get("/notes-debit", { params: buildParams(fd) });
    setDataD(data);
  }, [fd]);
  const loadP = useCallback(async () => {
    const { data } = await api.get("/notes-apurement", { params: buildParams(fp) });
    setDataP(data);
  }, [fp]);

  useEffect(() => { loadA(); }, [loadA]);
  useEffect(() => { loadD(); }, [loadD]);
  useEffect(() => { loadP(); }, [loadP]);

  const totalD = dataD.reduce((s, n) => s + Number(n.montant || 0), 0);
  const totalP = dataP.reduce((s, n) => s + Number(n.montant_apurement || 0), 0);

  return (
    <div data-testid="rapports-page">
      <div className="no-print">
        <PageHeader title="Rapports" subtitle="Rapports détaillés avec impression" />
      </div>
      <div className="print-only mb-4 text-center">
        <img src="/logo.png" alt="ONT" className="mx-auto h-16 w-16 object-contain" />
        <h2 className="font-heading text-xl font-bold">Office National du Tourisme — Rapport</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 no-print">
          <TabsTrigger value="assujettis" data-testid="rtab-assujettis"><Users className="mr-1.5 h-4 w-4" /> Assujettis</TabsTrigger>
          <TabsTrigger value="debit" data-testid="rtab-debit"><FileText className="mr-1.5 h-4 w-4" /> Notes de Perception</TabsTrigger>
          <TabsTrigger value="apurement" data-testid="rtab-apurement"><FileCheck2 className="mr-1.5 h-4 w-4" /> Notes d'Apurement</TabsTrigger>
        </TabsList>

        <TabsContent value="assujettis">
          <Card className="mb-4 border-slate-200 p-4 shadow-sm no-print">
            <Filters actes={actes} assujettis={assujettis} provinces={provinces} annees={annees} filters={fa} setFilters={setFa} showProvince />
          </Card>
          <Card className="border-slate-200 shadow-sm print-area">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead><TableHead>Redevable</TableHead><TableHead>Acte</TableHead>
                    <TableHead>Province</TableHead><TableHead>Ville</TableHead><TableHead>Commune</TableHead><TableHead>Quartier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataA.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">{a.code}</TableCell>
                      <TableCell className="font-medium text-slate-800">{a.redevable}</TableCell>
                      <TableCell>{a.acte_generateur_nom}</TableCell>
                      <TableCell>{a.province}</TableCell><TableCell>{a.ville}</TableCell>
                      <TableCell>{a.commune}</TableCell><TableCell>{a.quartier || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow><TableCell colSpan={7} className="text-sm">Total : {dataA.length} assujetti(s)</TableCell></TableRow>
                </TableFooter>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="debit">
          <Card className="mb-4 border-slate-200 p-4 shadow-sm no-print">
            <Filters actes={actes} assujettis={assujettis} provinces={provinces} annees={annees} filters={fd} setFilters={setFd} />
          </Card>
          <Card className="border-slate-200 shadow-sm print-area">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>N°</TableHead><TableHead>Redevable</TableHead><TableHead>Acte</TableHead>
                    <TableHead>Mois/Année</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataD.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell className="font-mono text-xs">{n.numero}</TableCell>
                      <TableCell className="font-medium text-slate-800">{n.redevable}</TableCell>
                      <TableCell>{n.acte_generateur_nom}</TableCell>
                      <TableCell>{n.mois} {n.annee}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(n.date)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMontant(n.montant, n.monnaie)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow><TableCell colSpan={5} className="text-right font-semibold">Total Débit</TableCell><TableCell className="text-right font-mono font-bold">{fmtMontant(totalD)}</TableCell></TableRow>
                </TableFooter>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="apurement">
          <Card className="mb-4 border-slate-200 p-4 shadow-sm no-print">
            <Filters actes={actes} assujettis={assujettis} provinces={provinces} annees={annees} filters={fp} setFilters={setFp} />
          </Card>
          <Card className="border-slate-200 shadow-sm print-area">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>N°</TableHead><TableHead>Redevable</TableHead><TableHead>Acte</TableHead>
                    <TableHead>Mois</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Montant Apuré</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataP.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell className="font-mono text-xs">{n.numero}</TableCell>
                      <TableCell className="font-medium text-slate-800">{n.redevable}</TableCell>
                      <TableCell>{n.acte_generateur_nom}</TableCell>
                      <TableCell>{n.mois}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(n.date)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMontant(n.montant_apurement, n.monnaie)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow><TableCell colSpan={5} className="text-right font-semibold">Total Apuré</TableCell><TableCell className="text-right font-mono font-bold">{fmtMontant(totalP)}</TableCell></TableRow>
                </TableFooter>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
